import type { ShowcaseProject } from '../../lib/api';
import { FireIcon, HeartIcon } from '../icons';
import { getProjectIconAsset } from '../../data/projectIcons';
import { getDisplayProjectMetaLabel } from '../../lib/projectMeta';
import { haptic } from '../../utils/haptic';
import { useAppStore } from '../../store/useAppStore';

function ShowcaseSvgIcon({ iconId }: { iconId?: string }) {
  const icon = getProjectIconAsset(iconId);
  return (
    <div style={{
      width: 36,
      height: 36,
      borderRadius: 10,
      background: icon.bg,
      border: `1px solid ${icon.color}30`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <img
        src={icon.src}
        alt=""
        width={22}
        height={22}
        draggable={false}
        style={{ width: 22, height: 22, display: 'block', pointerEvents: 'none', userSelect: 'none' }}
      />
    </div>
  );
}

interface ProjectCardProps {
  project: ShowcaseProject;
  onClick: () => void;
}

/** Short description from category/dev_time/price (max 2 lines). */
function getDescriptionLine(project: ShowcaseProject): string {
  const parts: string[] = [];
  if (project.category) parts.push(project.category);
  if (project.dev_time) parts.push(project.dev_time);
  if (project.price) parts.push(project.price);
  return parts.join(' · ') || '—';
}

/** CSS class for status badge by label (цветные, «горящие»). */
function getStatusBadgeClass(label: string): string {
  const lower = label.toLowerCase();
  if (lower.includes('прода') || lower.includes('sale') || lower.includes('mvp')) return 'showcase-card__badge showcase-card__badge--sale';
  if (lower.includes('партн') || lower.includes('partner')) return 'showcase-card__badge showcase-card__badge--partner';
  if (lower.includes('кейс') || lower.includes('case')) return 'showcase-card__badge showcase-card__badge--case';
  return 'showcase-card__badge';
}

/** Format price for display (e.g. 150000 → 150K P). */
function formatPrice(price: string | null): string {
  if (!price || !price.trim()) return '—';
  const num = parseInt(price.replace(/\D/g, ''), 10);
  if (Number.isNaN(num)) return price;
  if (num >= 1000) return `${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}K P`;
  return `${num} P`;
}

export function ProjectCard({ project, onClick }: ProjectCardProps) {
  const description = getDescriptionLine(project);
  const statusLabel = getDisplayProjectMetaLabel(project.project_status ?? project.statuses?.[0]) ?? 'Опубликован';
  const totalReactionCount = Math.max(0, project.reactions_count ?? 0);
  const rawIdeaCount = (project as ShowcaseProject & { idea_count?: number }).idea_count;
  const hasTypedBreakdown = typeof rawIdeaCount === 'number';
  const fireCount = totalReactionCount;
  const heartCount = hasTypedBreakdown ? Math.max(0, rawIdeaCount ?? 0) : totalReactionCount;
  const tools = Array.isArray(project.tools) ? project.tools : [];

  return (
    <article
      className="showcase-card"
      role="button"
      tabIndex={0}
      onClick={() => { haptic.tap(); onClick(); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          haptic.tap();
          onClick();
        }
      }}
      aria-label={`Проект: ${project.title}`}
    >
      <div className="showcase-card__header">
        <span className="showcase-card__emoji" aria-hidden>
          <ShowcaseSvgIcon iconId={(project as ShowcaseProject & { emoji?: string }).emoji} />
        </span>
        <div className="showcase-card__title-wrap">
          <h3 className="showcase-card__title">{project.title}</h3>
          <p
            className="showcase-card__author"
            onClick={(e) => {
              if (project.author?.id) {
                e.stopPropagation();
                haptic.tap();
                useAppStore.getState().setViewProfileUserId(project.author.id);
                useAppStore.getState().setScreen('user-profile');
              }
            }}
            style={project.author?.id ? { cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.2)' } : undefined}
          >
            {project.author?.username ? `@${project.author.username}` : 'Автор'}
          </p>
        </div>
        <span className={getStatusBadgeClass(statusLabel)}>{statusLabel}</span>
      </div>
      <p className="showcase-card__description" title={description}>
        {description}
      </p>
      {tools.length > 0 && (
        <div className="showcase-card__tags">
          {tools.slice(0, 5).map((t) => (
            <span key={t} className="showcase-card__tag">
              {t}
            </span>
          ))}
        </div>
      )}
      <div className="showcase-card__metrics">
        <span className="showcase-card__metric showcase-card__metric--time" title="Время разработки">
          <span className="showcase-card__metric-icon" aria-hidden>⏱</span>
          {project.dev_time || '—'}
        </span>
        <span className="showcase-card__metric showcase-card__metric--price" title="Цена">
          <span className="showcase-card__metric-icon" aria-hidden>💰</span>
          {formatPrice(project.price)}
        </span>
        <span className="showcase-card__metric showcase-card__metric--fire" title="Реакции">
          <FireIcon size={12} style={{ marginRight: 2, verticalAlign: 'middle' }} />
          {fireCount}
        </span>
        <span className="showcase-card__metric showcase-card__metric--heart" title="Нравится">
          <HeartIcon size={12} style={{ marginRight: 2, verticalAlign: 'middle' }} />
          {heartCount}
        </span>
      </div>
    </article>
  );
}
